/**
 * Combat qi pool (ลมปราณสะสม, docs/design/2026-10-11-gameplay-loop.md Fix A): "fight to gather, sit to refine".
 * A kill pays only `QI_POOL.direct` of its cultivation at once; `fill` x the rest goes into this pool (the part
 * world.ts paid is taken back here from the 'reward' event). Meditating, idle sit included, converts the pool at
 * `CULTIVATION.meditateRate` a second; with the pool empty it pays only `QI_POOL.emptyRate`; standing pays
 * `QI_POOL.standRate`. CultivationSystem asks `rate()` for the base rate each tick. The pool is saved on the progress (`qiPool`, optional), so a relog keeps it.
 */
import { CULTIVATION, QI_POOL } from '../cultivation';
import type { WorldEvent } from '../world';
import type { SystemHost, WorldSystem } from './types';

export class QiPoolSystem implements WorldSystem {
  readonly name = 'qiPool';
  /** Qi waiting to be refined (cultivation points); lives on the progress (`qiPool`) so it is saved. */
  get pool(): number {
    return this.h.world.progress.qiPool ?? 0;
  }
  set pool(v: number) {
    this.h.world.progress.qiPool = v;
  }
  constructor(private readonly h: SystemHost) {}

  observe(e: WorldEvent): void {
    if (e.type !== 'reward' || e.cultivation <= 0) return;
    const w = this.h.world;
    const held = Math.min(w.progress.cultivation, Math.round(e.cultivation * (1 - QI_POOL.direct)));
    w.progress.cultivation -= held;
    this.pool = Math.min(QI_POOL.cap, this.pool + (e.cultivation * (1 - QI_POOL.direct)) * QI_POOL.fill);
  }

  /** Qi put into the pool from outside the kill path (duel pay, systems/duelPay.ts). */
  add(q: number): void {
    this.pool = Math.min(QI_POOL.cap, this.pool + Math.max(0, q));
  }

  /** The base cultivation rate for the next `dt` seconds (drains the pool while meditating). */
  rate(meditating: boolean, dt: number): number {
    if (!meditating) return QI_POOL.standRate;
    const conv = Math.min(this.pool, CULTIVATION.meditateRate * dt);
    this.pool -= conv;
    const rest = Math.max(0, dt - conv / CULTIVATION.meditateRate);
    return dt > 0 ? (conv + QI_POOL.emptyRate * rest) / dt : QI_POOL.emptyRate;
  }
}
